import { AfterViewInit, Component, ElementRef, Renderer2, ViewChild } from '@angular/core';
import SignaturePad from 'signature_pad';
import { Router } from '@angular/router';
import { ballotRoute, reviewRoute } from '../route-names';
import { BehaviorSubject } from 'rxjs';
import { BallotService } from '../ballot/ballot.service';
import imageCompression from 'browser-image-compression';
import { BrowserSupportService } from '../browser-support/browser-support.service';
import { MatDialog } from '@angular/material/dialog';
import { ExitModalComponent } from '../exit-modal/exit-modal.component';
import { BallotConfirmAmComponent } from '../ballot/ballot-confirm-am.component';
import { ApiAboutService } from '../Services/api-about.service';
import { environment } from "src/environments/environment";
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Component({
    selector: 'app-signature',
    templateUrl: './signature.component.html',
    styleUrls: ['./signature.component.scss'],
    standalone: false
})
export class SignatureComponent implements AfterViewInit {
  @ViewChild('canvas') canvas!: ElementRef;
  public signaturePad!: SignaturePad;
  readonly ballot = this.ballotService.getBallot();
  readonly signature = new BehaviorSubject<string>('');
  readonly isEmpty = new BehaviorSubject<boolean>(true);
  readonly isPINincomplete = new BehaviorSubject<boolean>(true);
  width = 0;
  checked: boolean = false;
  signaturePIN: string = "";
  affidavitText = $localize`I swear or affirm, under penalty of perjury that: I am eligible to cast a ballot in this election, I am not qualified to vote elsewhere, and I have not and will not vote elsewhere in this election.`
  routeName = "";
  markXSignature: boolean = environment.markSignatureAsX;
  xImgURL: Promise<string>;
  showButtons = true

  constructor(
    private renderer: Renderer2,
    private readonly router: Router,
    private readonly ballotService: BallotService,
    private readonly browserSupportService: BrowserSupportService,
    private readonly dialog: MatDialog,
    private readonly aboutService: ApiAboutService,
    private announcer: LiveAnnouncer,
    ) {
      this.xImgURL = this.imageToDataURL('../assets/images/xSignature.png');
    }

  ngAfterViewInit(): void {
    this.routeName = this.router.url
    this.resetFocus()
    this.affidavitText = this.ballotService.ballot?.voteEventData.optionalEventConfiguration?.defaultAffidavitText ?? (this.aboutService.aboutInformation.value.org_feature_set.declarationSettings?.declarationText ?? this.affidavitText);
    this.signaturePad = new SignaturePad(this.renderer.selectRootElement(this.canvas['nativeElement']), {
      backgroundColor: '#fff'
    });
    this.onResize();
    this.clear();
    this.width = window.innerWidth;
     this.announcer.announce($localize `Affidavit Use the screenreader to read the affirmation text`)
  }

  async imageToDataURL(imagePath: string): Promise<string> {
    const res = await fetch(imagePath);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => {
        const dataURL = reader.result as string;
        resolve(dataURL);
      };

      reader.onerror = (error) => {
        reject(error);
      };
    });
  }

  resetFocus() {
    let focusField = document.getElementById('accessibility-button')
    let focusFieldMobile = document.getElementById('accessibility-button-mobile')
  
    if (focusField) {
      focusField.focus()
    }

    if (focusFieldMobile) {
     focusFieldMobile.focus()
    }
  }

  onResize(): void {
    if(window.innerWidth !== this.width){
      var ratio = Math.max(window.devicePixelRatio || 1, 1);
      this.canvas.nativeElement.width = this.canvas.nativeElement.offsetWidth * ratio;
      this.canvas.nativeElement.height = this.canvas.nativeElement.offsetHeight * ratio;
      this.canvas.nativeElement.getContext('2d').scale(ratio, ratio);
      this.signaturePad.backgroundColor = '#fff';
      if(!this.browserSupportService.resizeCanvasSupported) {
        this.clear();
      }
    }
  }

  clear() {
    this.signaturePad.clear();
    this.isEmpty.next(this.signaturePad.isEmpty());
  }

  undo() {
    let data = this.signaturePad.toData();
    if (data) {
      data.pop();
      this.signaturePad.fromData(data);
    }
    this.isEmpty.next(this.signaturePad.isEmpty());
  }

  exit(): void {
    this.dialog.open(ExitModalComponent)
  }

  returnToBallot() {
    this.router.navigate(ballotRoute);
  }

  goToReview() {
    this.router.navigate(reviewRoute);
  }

  touchedCanvas() {
    this.isEmpty.next(this.signaturePad.isEmpty());
  }

  async compressImage() {
    const data = this.signaturePad.toDataURL('image/jpeg');
    const imageFile = await imageCompression.getFilefromDataUrl(data, 'Test');
    const compressedImageFile = await imageCompression(imageFile,{ maxSizeMB: 0.05 });
    const compressedFileURL = await imageCompression.getDataUrlFromFile(compressedImageFile);
    //converts Base64URI to Base64
    this.signature.next(compressedFileURL.replace(/-/g, '+').replace(/_/g, '/').replace(/^data:image\/[a-z]+;base64,/, ''));
    return this.signature.getValue();
  }

  async openSubmitModal(event: any) {
    event.preventDefault();
    let compressedImageEncoding = '';
    await this.compressImage().then(image => compressedImageEncoding = image);
    this.signature.next(compressedImageEncoding);
    this.dialog.open(BallotConfirmAmComponent, {
      data: {ballot: this.ballot, signature: compressedImageEncoding}
    })
  }

  async handleCheck() {
    if (this.checked) {
      this.showButtons = false;
      this.signaturePad.fromDataURL(await this.xImgURL);
      this.announcer.announce($localize `An x will be drawn on the signature pad to indicate that you are unable to sign.`)

    } else {
      this.showButtons = true;
      this.clear(); 
      this.signaturePIN = ''
    }

    if (this.checked && this.signaturePIN?.length == 6){
      this.isPINincomplete.next(false);      
    } else {
      this.isPINincomplete.next(true);
    }
  }

  async handleInput() {
    this.checked = true;
    if (this.checked && this.signaturePIN?.length == 6){
      this.isPINincomplete.next(false);
    } else {
      this.isPINincomplete.next(true);
    }
  }
}
